import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { saveAs } from 'file-saver';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import authService from '../../api-authorization/AuthorizeService';
import { Link} from 'react-router-dom'

const OnlineGameDownloader = () => {
    const [playerName, setPlayerName] = useState('');
    const [lichessUsername, setLichessUsername] = useState('');
    const [chessComId, setChessComId] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [isSupporter, setIsSupporter] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');

    const GetMySupporterStatus = async (token) => {
        var headers = {
            headers: { 'Authorization': `Bearer ${token}` }
        }
        axios.get('/api/NACHUsers/GetMySupporterStatusExpDateUtc', headers).then((response) => {
            setIsSupporter(response.data.isSupporter);
            return response.data.isSupporter;
        }).catch((error) => {
            console.log(JSON.stringify(error));
            setIsSupporter(false);
            return false;
        });
    };

    useEffect(async () => {
        var token = await authService.getAccessToken();
        await GetMySupporterStatus(token);
    }, []);

    const handlePlayerNameChange = (event) => {
        setPlayerName(event.target.value);
    };

    const handleLichessUsernameChange = (event) => {
        setLichessUsername(event.target.value);
    };

    const handleChessComIdChange = (event) => {
        setChessComId(event.target.value);
    };

    const handleDownload = async () => {
        try {
            if (!isSupporter) {
                setErrorMessage('This is a support only feature. ');
                setOpenDialog(true);
                return;
            }
            // Validate input
            if (!playerName) {
                setErrorMessage('Player Name is required.');
                setOpenDialog(true);
                return;
            }

            if (!lichessUsername && !chessComId) {
                setErrorMessage('Either Lichess ID or Chess.com ID is required.');
                setOpenDialog(true);
                return;
            }

            // Helper function to fetch Lichess games with proper error handling
            const fetchLichessGames = async (username) => {
                setProgressMessage('Fetching Lichess games...');
                
                // Use backend proxy to avoid CORS issues
                const token = await authService.getAccessToken();
                const lichessResponse = await fetch(`/api/OnlineGamesProxy/lichess/games/${username}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (!lichessResponse.ok) {
                    const errorText = await lichessResponse.text();
                    if (lichessResponse.status === 404) {
                        throw new Error(errorText || `User ${username} not found on Lichess.`);
                    }
                    if (lichessResponse.status === 429) {
                        throw new Error('Lichess rate limit exceeded. Please try again later.');
                    }
                    throw new Error(`Lichess API error: ${lichessResponse.status} ${lichessResponse.statusText}`);
                }
                
                const lichessData = await lichessResponse.text();
                setProgressMessage('');
                if (!lichessData || lichessData.trim().length === 0) {
                    throw new Error(`No games found for Lichess user ${username}.`);
                }
                return lichessData;
            };

            // Helper function to fetch Chess.com games with proper error handling
            const fetchChessComGames = async (userId) => {
                setProgressMessage('Fetching Chess.com games...');
                
                // Use backend proxy to avoid CORS issues
                const token = await authService.getAccessToken();
                const chessComResponse = await fetch(`/api/OnlineGamesProxy/chesscom/allgames/${userId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (!chessComResponse.ok) {
                    const errorText = await chessComResponse.text();
                    if (chessComResponse.status === 404) {
                        throw new Error(errorText || `User ${userId} not found on Chess.com.`);
                    }
                    if (chessComResponse.status === 429) {
                        throw new Error('Chess.com rate limit exceeded. Please try again later.');
                    }
                    throw new Error(`Chess.com API error: ${chessComResponse.status} ${chessComResponse.statusText}`);
                }
                
                const pgnContent = await chessComResponse.text();
                setProgressMessage('');
                
                if (!pgnContent || pgnContent.trim().length === 0) {
                    throw new Error(`No games found for Chess.com user ${userId}.`);
                }
                
                return pgnContent;
            };

            // Download games based on input
            if (lichessUsername && !chessComId) {
                setLoading(true);
                try {
                    const lichessData = await fetchLichessGames(lichessUsername);
                    const replacedPgn = lichessData.replace(new RegExp(lichessUsername, 'gi'), playerName);
                    const blob = new Blob([replacedPgn], { type: 'text/plain' });
                    saveAs(blob, `${playerName + "_lichess_"+lichessUsername}_games.pgn`);
                } catch (err) {
                    setErrorMessage(err.message);
                    setOpenDialog(true);
                }
                setLoading(false);
                return;
            }

            if (!lichessUsername && chessComId) {
                setLoading(true);
                try {
                    const chessComPgnContent = await fetchChessComGames(chessComId);
                    const replacedPgn = chessComPgnContent.replace(new RegExp(chessComId, 'gi'), playerName);
                    const blob = new Blob([replacedPgn], { type: 'text/plain' });
                    saveAs(blob, `${playerName+"_chess.com_" + chessComId}_games.pgn`);
                } catch (err) {
                    setErrorMessage(err.message);
                    setOpenDialog(true);
                }
                setLoading(false);
                return;
            }

            if (lichessUsername && chessComId) {
                setLoading(true);
                try {
                    const lichessData = await fetchLichessGames(lichessUsername);
                    const lichessReplacedPgn = lichessData.replace(new RegExp(lichessUsername, 'gi'), playerName);

                    const chessComPgnContent = await fetchChessComGames(chessComId);
                    const chessComReplacedPgn = chessComPgnContent.replace(new RegExp(chessComId, 'gi'), playerName);

                    const mergedPgnContent = `${lichessReplacedPgn}\n\n${chessComReplacedPgn}`;
                    const blob = new Blob([mergedPgnContent], { type: 'text/plain' });
                    saveAs(blob, `${playerName + "_chess.com_" + chessComId + "_lichess_" + lichessUsername}_games.pgn`);
                } catch (err) {
                    setErrorMessage(err.message);
                    setOpenDialog(true);
                }
                setLoading(false);
                return;
            }
        } catch (error) {
            console.error('Error:', error.message);
            setErrorMessage('An error occurred. Please try again.');
            setOpenDialog(true);
            setProgressMessage('');
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <Box sx={{ width: '100%', mt: 2, mb: 1, }}>
        <div className="Container" style={{ position: 'relative' }}>
            <Accordion m={2} pt={3} pl={3} pr={3} defaultExpanded={true}>
                <AccordionSummary aria-controls="event-info" id="event-info" expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#1976d2' }}>
                    <Typography color='white'>Download Player's Online Games</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ pt: 2, pb: 2 }}>
                        <Grid container direction="row" rowSpacing={1} columnSpacing={1} alignItems="center">
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label="Player Name"
                                    variant="outlined"
                                    margin="normal"
                                    value={playerName}
                                    onChange={handlePlayerNameChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label={
                                        <span>
                                            <a href={'https://chess.com'} target='_blank' rel='noreferrer'>
                                                Chess.com
                                            </a>&nbsp;
                                            <a href={'https://docs.google.com/spreadsheets/d/1c0GRuLXw-OUB5mJR_OLGKuvJqJw2UU2Kaju94GRgl60/gviz/tq?tqx=out:csv&sheet=PlayerDatabase'} target='_blank' rel='noreferrer'>
                                                Player ID
                                            </a>
                                            <a href={'https://www.chess.com/member/' + chessComId} target='_blank' rel='noreferrer'>{' ' + chessComId}</a>
                                        </span>
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    value={chessComId}
                                    onChange={handleChessComIdChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label={
                                        <span>
                                            <a href={'https://lichess.org'} target='_blank' rel='noreferrer'>
                                                Lichess.org ID
                                            </a>&nbsp;
                                            <a href={'https://lichess.org/@/' + lichessUsername} target='_blank' rel='noreferrer'>{' ' + lichessUsername}</a>
                                        </span>
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    value={lichessUsername}
                                    onChange={handleLichessUsernameChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3} >
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownload}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <CircularProgress size={24} color="inherit" />
                                            {progressMessage || 'Downloading...'}
                                        </span>
                                    ) : 'Download Games'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle>Error</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                    {errorMessage}
                            </DialogContentText>
                        </DialogContent>
                            <DialogActions>
                                <Stack fullwidth m={1} direction={{ xs: "column", sm: "column", md: "row" }} spacing={1} alignItems="center">
                                    {!isSupporter &&
                                        <Link to="/SupporterDonate">
                                                <Button color="secondary" variant='contained'>Support MetaDB</Button>
                                        </Link>
                                    }
                                    <Button onClick={handleCloseDialog} color="primary" variant="contained">
                                        Close
                                    </Button>
                                </Stack>
                        </DialogActions>
                    </Dialog>
                </AccordionDetails>
            </Accordion>
        </div>
            </Box>
            );
};

export default OnlineGameDownloader;
